/**
 * SecurityAlerts Component
 * Displays security alerts and anomalies detected in the system
 */

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Shield, 
  Clock, 
  User, 
  Eye, 
  ChevronDown, 
  ChevronRight,
  Bell,
  CheckCircle,
  X
} from 'lucide-react';
import { anomalyDetector, SEVERITY_LEVELS } from '../../utils/anomalyDetection';
import { useTheme, getThemeStyles } from '../../contexts/ThemeContext';
import { usePermissions, hasPermission, PERMISSIONS } from '../../utils/permissions';

const SecurityAlerts = ({ logs = [], isVisible = false, onClose }) => {
  const { isDarkMode } = useTheme();
  const themeStyles = getThemeStyles(isDarkMode);
  const { permissions } = usePermissions();
  
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  useEffect(() => {
    if (isVisible && hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS)) {
      analyzeForAnomalies();
      loadAlertSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, logs, permissions]);

  const analyzeForAnomalies = async () => {
    setLoading(true);
    try {
      const detectedAnomalies = await anomalyDetector.detectAnomalies(logs, {
        timeWindow: 86400000 // 24 hours
      });
      setAlerts(detectedAnomalies);
    } catch (error) {
      console.error('Error analyzing for anomalies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlertSummary = async () => {
    try {
      const alertSummary = await anomalyDetector.getAlertSummary();
      setSummary(alertSummary);
    } catch (error) {
      console.error('Error loading alert summary:', error);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case SEVERITY_LEVELS.CRITICAL:
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case SEVERITY_LEVELS.HIGH:
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case SEVERITY_LEVELS.MEDIUM:
        return <Shield className="h-5 w-5 text-yellow-600" />;
      case SEVERITY_LEVELS.LOW:
        return <Bell className="h-5 w-5 text-blue-600" />;
      default:
        return <Bell className="h-5 w-5 text-gray-600" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case SEVERITY_LEVELS.CRITICAL:
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-800';
      case SEVERITY_LEVELS.HIGH:
        return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-800';
      case SEVERITY_LEVELS.MEDIUM:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-800';
      case SEVERITY_LEVELS.LOW:
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-800';
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 80) return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900';
    if (score >= 60) return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900';
    return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900';
  };

  const filteredAlerts = selectedSeverity === 'all' 
    ? alerts 
    : alerts.filter(alert => alert.severity === selectedSeverity);

  if (!isVisible) return null;

  if (!hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${themeStyles.bg.primary} rounded-lg shadow-xl p-8 text-center`}>
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className={`text-lg font-semibold ${themeStyles.text.primary} mb-2`}>
            Access Denied
          </h3>
          <p className={`${themeStyles.text.secondary} mb-4`}>
            You don't have permission to view security alerts.
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${themeStyles.bg.primary} rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className={`${themeStyles.bg.secondary} px-6 py-4 border-b ${themeStyles.border.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h2 className={`text-xl font-semibold ${themeStyles.text.primary}`}>
                Security Alerts
              </h2>
              {summary && (
                <span className={`px-2 py-1 text-xs rounded-full ${getRiskScoreColor(summary.riskScore)}`}>
                  Risk Score: {summary.riskScore}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className={`${themeStyles.text.secondary} hover:${themeStyles.text.primary}`}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {summary.total}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Alerts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {summary.bySeverity.critical}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Critical</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {summary.bySeverity.high}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">High</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {summary.bySeverity.medium}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Medium</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {summary.bySeverity.low}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Low</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <label className={`text-sm font-medium ${themeStyles.text.primary}`}>
              Filter by Severity:
            </label>
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className={`px-3 py-1 rounded border ${themeStyles.border.primary} ${themeStyles.bg.secondary} ${themeStyles.text.primary}`}
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className={`ml-3 ${themeStyles.text.primary}`}>Analyzing logs for anomalies...</span>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center p-8">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h3 className={`text-lg font-medium ${themeStyles.text.primary} mb-2`}>
                No Alerts Found
              </h3>
              <p className={`${themeStyles.text.secondary}`}>
                No security anomalies detected in the selected time period.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {filteredAlerts.map((alert, index) => (
                <div
                  key={alert.id || index}
                  className={`border rounded-lg ${themeStyles.border.primary} ${themeStyles.bg.secondary}`}
                >
                  {/* Alert Header */}
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {getSeverityIcon(alert.severity)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className={`font-medium ${themeStyles.text.primary}`}>
                              {alert.description}
                            </h4>
                            <span className={`px-2 py-1 text-xs rounded border ${getSeverityColor(alert.severity)}`}>
                              {alert.severity.toUpperCase()}
                            </span>
                            {alert.riskScore && (
                              <span className={`px-2 py-1 text-xs rounded ${getRiskScoreColor(alert.riskScore)}`}>
                                Risk: {alert.riskScore}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                            <span className="flex items-center space-x-1">
                              <Clock className="h-4 w-4" />
                              <span>{new Date(alert.timestamp).toLocaleString()}</span>
                            </span>
                            {alert.userId && (
                              <span className="flex items-center space-x-1">
                                <User className="h-4 w-4" />
                                <span>{alert.userId}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {expandedAlert === alert.id ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Alert Details */}
                  {expandedAlert === alert.id && (
                    <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="pt-4">
                        <h5 className={`font-medium ${themeStyles.text.primary} mb-3`}>
                          Alert Details
                        </h5>
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="font-medium text-gray-500 dark:text-gray-400">Type:</span>
                              <span className={`ml-2 ${themeStyles.text.primary}`}>{alert.type}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-500 dark:text-gray-400">Severity:</span>
                              <span className={`ml-2 ${themeStyles.text.primary}`}>{alert.severity}</span>
                            </div>
                          </div>
                          
                          {alert.details && (
                            <div className="mt-4">
                              <h6 className={`font-medium ${themeStyles.text.primary} mb-2`}>
                                Technical Details
                              </h6>
                              <div className={`p-3 rounded ${themeStyles.bg.primary} text-sm`}>
                                <pre className={`whitespace-pre-wrap ${themeStyles.text.secondary}`}>
                                  {JSON.stringify(alert.details, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${themeStyles.bg.secondary} px-6 py-4 border-t ${themeStyles.border.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <Eye className="h-4 w-4" />
              <span>Showing {filteredAlerts.length} of {alerts.length} alerts</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityAlerts;
